import * as turf from '@turf/turf';
import type { Feature, Polygon } from 'geojson';

import { env } from '@/config/env';

import { isAnalysisPolygonRingValid } from './analysisRegionDrawingGeometry';

/** Default max AOI area km2 (spec 6a). */
export const SAR_AOI_MAX_AREA_KM2_DEFAULT = 10_000;

export function getSarAoiMaxAreaKm2(): number {
  const n = env.SAR_AOI_MAX_AREA_KM2;
  return n > 0 ? n : SAR_AOI_MAX_AREA_KM2_DEFAULT;
}

/** Close open [lng, lat][] ring into a GeoJSON Polygon (repeat first vertex at end). */
export function openLngLatRingToPolygonFeature(
  openRing: [number, number][],
): Feature<Polygon> | null {
  if (openRing.length < 3) return null;
  const closed = [...openRing, openRing[0]];
  try {
    return turf.polygon([closed]);
  } catch {
    return null;
  }
}

/** `min_lon,min_lat,max_lon,max_lat` — dùng SAR preview Gateway. */
export function openRingToBboxCsv(ring: [number, number][]): string | null {
  if (ring.length < 1) return null;
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    minLon = Math.min(minLon, lng);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lng);
    maxLat = Math.max(maxLat, lat);
  }
  if (!Number.isFinite(minLon) || !Number.isFinite(minLat)) return null;
  return `${minLon},${minLat},${maxLon},${maxLat}`;
}

/** Area in km2 (Turf returns m2). */
export function computeAoiAreaKm2(openRing: [number, number][]): number | null {
  const poly = openLngLatRingToPolygonFeature(openRing);
  if (!poly) return null;
  return turf.area(poly) / 1_000_000;
}

export function isOpenRingAreaWithinMaxKm2(
  openRing: [number, number][],
  maxKm2: number = getSarAoiMaxAreaKm2(),
): boolean {
  const km2 = computeAoiAreaKm2(openRing);
  if (km2 == null) return false;
  return km2 <= maxKm2;
}

/** Open ring is valid (no self-intersect) and area within max km2. */
export function isValidSarAoiOpenRing(
  openRing: [number, number][],
  maxKm2: number = getSarAoiMaxAreaKm2(),
): boolean {
  if (openRing.length < 3 || !isAnalysisPolygonRingValid(openRing)) return false;
  return isOpenRingAreaWithinMaxKm2(openRing, maxKm2);
}

/** Pad bbox ~1.5% per side (+ epsilon) so AOI edges are not flush with GEE tile bounds. */
const ALPHA_EARTH_BBOX_PAD_RATIO = 0.015;
const ALPHA_EARTH_BBOX_PAD_EPS_DEG = 0.0005;

/**
 * `minLng,minLat,maxLng,maxLat` for `GET /api/alpha-earth/tiles` (`bbox` query).
 * Requires at least two points; a single point returns `null`.
 */
export function openLngLatPointsToAlphaEarthBboxCsv(points: [number, number][]): string | null {
  if (points.length < 2) return null;
  try {
    const line = turf.lineString(points as [number, number][]);
    const box = turf.bbox(line);
    let [w, s, e, n] = box;
    const dx = (e - w) * ALPHA_EARTH_BBOX_PAD_RATIO + ALPHA_EARTH_BBOX_PAD_EPS_DEG;
    const dy = (n - s) * ALPHA_EARTH_BBOX_PAD_RATIO + ALPHA_EARTH_BBOX_PAD_EPS_DEG;
    w -= dx;
    e += dx;
    s -= dy;
    n += dy;
    if (!(w < e && s < n)) return null;
    const fmt = (x: number) => x.toFixed(6);
    return `${fmt(w)},${fmt(s)},${fmt(e)},${fmt(n)}`;
  } catch {
    return null;
  }
}

/**
 * Bbox CSV for Alpha Earth from a GeoJSON Polygon exterior (e.g. job `aoiPolygon`).
 */
export function polygonToAlphaEarthBboxCsv(geometry: Polygon): string | null {
  if (!geometry?.coordinates?.[0]?.length) return null;
  try {
    const poly = turf.feature(geometry);
    const box = turf.bbox(poly);
    let [w, s, e, n] = box;
    const dx = (e - w) * ALPHA_EARTH_BBOX_PAD_RATIO + ALPHA_EARTH_BBOX_PAD_EPS_DEG;
    const dy = (n - s) * ALPHA_EARTH_BBOX_PAD_RATIO + ALPHA_EARTH_BBOX_PAD_EPS_DEG;
    w -= dx;
    e += dx;
    s -= dy;
    n += dy;
    if (!(w < e && s < n)) return null;
    const fmt = (x: number) => x.toFixed(6);
    return `${fmt(w)},${fmt(s)},${fmt(e)},${fmt(n)}`;
  } catch {
    return null;
  }
}

// Backlog (spec): bbox draw mode; lock Deck controller while drawing AOI — plan spec-gap-phase2.
